const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const LOWER = 'abcdefghjkmnpqrstuvwxyz';
const DIGITS = '23456789';
const SYMBOLS = '!@#$%&*';

export interface PasswordStrength {
  score: number;
  label: string;
  checks: { label: string; passed: boolean }[];
}

export function getPasswordStrength(password: string): PasswordStrength {
  const checks = [
    { label: '8+ characters', passed: password.length >= 8 },
    { label: 'Uppercase letter', passed: /[A-Z]/.test(password) },
    { label: 'Lowercase letter', passed: /[a-z]/.test(password) },
    { label: 'Number or symbol', passed: /[\d!@#$%&*]/.test(password) },
  ];

  const score = checks.filter((c) => c.passed).length;

  const labels = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'];
  return { score, label: labels[score] ?? '', checks };
}

export const strengthColors: Record<number, string> = {
  0: 'bg-gray-200',
  1: 'bg-red-500',
  2: 'bg-orange-400',
  3: 'bg-yellow-400',
  4: 'bg-emerald-500',
};

export const strengthTextColors: Record<number, string> = {
  0: 'text-gray-400',
  1: 'text-red-500',
  2: 'text-orange-500',
  3: 'text-yellow-600',
  4: 'text-emerald-600',
};

/**
 * Generate a strong random password (12–16 chars: upper, lower, digits, symbols).
 * Meets backend min 8 chars; 12+ for security.
 */
export function generateSecurePassword(length: number = 14): string {
  const pool = UPPER + LOWER + DIGITS + SYMBOLS;
  const getRandom = (str: string) => str[Math.floor(Math.random() * str.length)];

  const parts = [
    getRandom(UPPER),
    getRandom(LOWER),
    getRandom(DIGITS),
    getRandom(SYMBOLS),
  ];

  for (let i = parts.length; i < length; i++) {
    parts.push(getRandom(pool));
  }

  for (let i = parts.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [parts[i], parts[j]] = [parts[j], parts[i]];
  }

  return parts.join('');
}
